import { motion } from 'framer-motion'
import { useLanguage } from '../contexts/LanguageContext'
import ScrollArrow from '../components/ScrollArrow'
import GlareCard from '../components/ui/GlareCard'
import ProgressiveImage from '../components/ui/ProgressiveImage'
import { textReveal, staggerContainer, staggerItem } from '../animations/variants'

const About = () => {
  const { t } = useLanguage()

  return (
    <div className="bg-black">
      {/* Hero Section - Full Screen */}
      <section className="section-fullscreen">
        <div className="section-bg">
          <ProgressiveImage
            src="/images/hero-about.jpg"
            placeholder="/images/placeholders/hero-about-placeholder.jpg"
            alt="Office"
          />
          <div className="section-overlay" />
          <div className="section-overlay-left" />
        </div>

        <div className="section-content">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.div variants={textReveal} className="section-label">ABOUT US</motion.div>
            <motion.h1 variants={textReveal} className="section-heading text-glow">
              {t('about.hero.title')}
            </motion.h1>
            <motion.p variants={textReveal} className="section-description">
              {t('about.hero.description')}
            </motion.p>
          </motion.div>
        </div>

        <ScrollArrow />
      </section>

      {/* Company Introduction - 网格纹理背景 */}
      <section className="relative z-10 py-24 px-6 md:px-20 bg-grid-texture">
        {/* 顶部渐变过渡 */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black to-transparent pointer-events-none" />
        {/* 底部渐变过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent pointer-events-none" />

        <div className="relative max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={staggerContainer}
              viewport={{ once: true }}
            >
              <motion.div variants={staggerItem} className="section-label mb-6">COMPANY PROFILE</motion.div>
              <motion.h2 variants={staggerItem} className="text-3xl md:text-4xl font-bold text-white mb-8 uppercase tracking-[-0.02em]">
                {t('about.intro.title')}
              </motion.h2>
              <motion.div variants={staggerItem} className="space-y-6 text-gray-400 font-mono text-sm leading-relaxed">
                <p>
                  <strong className="text-white">{t('about.intro.positioning')}</strong>
                  {t('about.intro.positioning.desc')}
                </p>
                <p>
                  <strong className="text-white">{t('about.intro.purpose')}</strong>
                  {t('about.intro.purpose.desc')}
                </p>
                <p>
                  <strong className="text-white">{t('about.intro.goal')}</strong>
                  {t('about.intro.goal.desc')}
                </p>
              </motion.div>
            </motion.div>

            <div className="grid grid-cols-2 gap-6">
              {[
                { icon: 'apartment', title: t('about.features.industrial'), desc: t('about.features.industrial.desc') },
                { icon: 'target', title: t('about.features.smart'), desc: t('about.features.smart.desc') },
                { icon: 'group', title: t('about.features.collaboration'), desc: t('about.features.collaboration.desc') },
                { icon: 'lightbulb', title: t('about.features.innovation'), desc: t('about.features.innovation.desc') }
              ].map((item, index) => (
                <GlareCard key={index}>
                  <span className="material-symbols-outlined text-2xl text-white/30 mb-4">{item.icon}</span>
                  <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-2">{item.title}</h3>
                  <p className="text-xs text-gray-500 font-mono">{item.desc}</p>
                </GlareCard>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technology Background - 渐变纹理背景 */}
      <section className="relative z-10 py-24 px-6 md:px-20 bg-gradient-texture">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div variants={textReveal} className="section-label justify-center flex mb-6">TECHNOLOGY</motion.div>
            <motion.h2 variants={textReveal} className="text-3xl md:text-4xl font-bold text-white mb-8 uppercase tracking-[-0.02em]">
              {t('about.tech.title')}
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="metal-surface p-8 md:p-12"
          >
            <motion.p variants={staggerItem} className="text-gray-400 font-mono text-sm leading-relaxed mb-8">
              {t('about.tech.intro')}
            </motion.p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { title: t('about.tech.digital.title'), desc: t('about.tech.digital.desc') },
                { title: t('about.tech.cloud.title'), desc: t('about.tech.cloud.desc') },
                { title: t('about.tech.automation.title'), desc: t('about.tech.automation.desc') },
                { title: t('about.tech.ai.title'), desc: t('about.tech.ai.desc') }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  className="p-6 border border-white/10 hover:border-white/30 transition-colors"
                >
                  <h3 className="font-bold text-white mb-3 uppercase tracking-widest text-sm">{item.title}</h3>
                  <p className="text-xs text-gray-500 font-mono">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Team Introduction - 深灰背景 */}
      <section className="relative z-10 py-24 px-6 md:px-20 bg-dark-gray">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={staggerContainer}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center"
        >
          <motion.div variants={textReveal} className="section-label justify-center flex mb-6">OUR TEAM</motion.div>
          <motion.h2 variants={textReveal} className="text-3xl md:text-4xl font-bold text-white mb-8 uppercase tracking-[-0.02em]">
            {t('about.team.title')}
          </motion.h2>

          <motion.div variants={staggerItem} className="metal-surface p-8 md:p-12">
            <p className="text-lg text-gray-400 font-mono leading-relaxed mb-12">
              {t('about.team.intro')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { title: t('about.team.tech.title'), desc: t('about.team.tech.desc') },
                { title: t('about.team.product.title'), desc: t('about.team.product.desc') },
                { title: t('about.team.industry.title'), desc: t('about.team.industry.desc') }
              ].map((item, index) => (
                <div key={index} className="p-6 border border-white/10">
                  <h3 className="font-bold text-white mb-2 uppercase tracking-widest text-sm">{item.title}</h3>
                  <p className="text-xs text-gray-500 font-mono">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </section>
    </div>
  )
}

export default About
